import { Link } from "@/lib/router";
import { Menu, HelpCircle } from "lucide-react";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useSidebar } from "../context/SidebarContext";
import { usePractice } from "../context/PracticeContext";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Fragment } from "react";

export function BreadcrumbBar() {
  const { breadcrumbs } = useBreadcrumbs();
  const { toggleSidebar, isMobile, sidebarOpen } = useSidebar();
  const { selectedPractice } = usePractice();

  if (breadcrumbs.length === 0) return null;

  const menuButton = (isMobile || !sidebarOpen) && (
    <Button
      variant="ghost"
      size="icon-sm"
      className="mr-2 shrink-0"
      onClick={toggleSidebar}
      aria-label="Open sidebar"
    >
      <Menu className="h-5 w-5" />
    </Button>
  );

  const practiceLogo = (
    <img
      src="/brands/vip-medical-group.png"
      alt={selectedPractice?.name ?? "VIP Medical Group"}
      className="h-14 w-auto object-contain shrink-0 mr-4 pr-4 border-r border-border"
    />
  );

  const rightSection = (
    <div className="flex items-center gap-3 ml-auto shrink-0">
      <Link to="/docs">
        <Button variant="ghost" size="icon-sm" aria-label="Help & Documentation">
          <HelpCircle className="h-4.5 w-4.5 text-muted-foreground" />
        </Button>
      </Link>

      <div className="flex items-center gap-3 pl-3 border-l border-border">
        <img src="/brands/ligament-icon.png" alt="Ligament" className="h-10 w-10 object-contain" />
        <div className="leading-tight hidden sm:block">
          <span className="text-base font-bold text-foreground tracking-tight">Ligament</span>
          <p className="text-[10px] text-muted-foreground leading-tight">The Connective Tissue of Healthcare</p>
        </div>
      </div>
    </div>
  );

  // Single breadcrumb = page title (uppercase)
  if (breadcrumbs.length === 1) {
    return (
      <div className="border-b border-border px-4 md:px-6 h-16 shrink-0 flex items-center min-w-0 overflow-hidden">
        {menuButton}
        {practiceLogo}
        <h1 className="text-sm font-semibold uppercase tracking-wider truncate">
          {breadcrumbs[0].label}
        </h1>
        {rightSection}
      </div>
    );
  }

  // Multiple breadcrumbs = breadcrumb trail
  return (
    <div className="border-b border-border px-4 md:px-6 h-16 shrink-0 flex items-center min-w-0 overflow-hidden">
      {menuButton}
      {practiceLogo}
      <Breadcrumb className="min-w-0 overflow-hidden">
        <BreadcrumbList className="flex-nowrap">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <Fragment key={i}>
                {i > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem className={isLast ? "min-w-0" : "shrink-0"}>
                  {isLast || !crumb.href ? (
                    <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.href}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      {rightSection}
    </div>
  );
}
